import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { settingsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { Save, Search, Code, Globe } from 'lucide-react';

export const AdminSEO = () => {
  const [settings, setSettings] = useState(null);
  const [schemaText, setSchemaText] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchSettings();
  }, []);

  const fetchSettings = async () => {
    try {
      const response = await settingsAPI.get();
      setSettings(response.data);
      setSchemaText(JSON.stringify(response.data.schema_data || {}, null, 2));
    } catch (e) {
      toast.error('Failed to load settings');
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      let schemaData = {};
      try {
        schemaData = JSON.parse(schemaText);
      } catch (e) {
        toast.error('Invalid JSON in schema data');
        setSaving(false);
        return;
      }

      await settingsAPI.update({
        ...settings,
        schema_data: schemaData,
      });
      toast.success('SEO settings saved!');
    } catch (e) {
      toast.error('Failed to save settings');
    } finally {
      setSaving(false);
    }
  };

  const updateField = (field, value) => {
    setSettings((prev) => ({ ...prev, [field]: value }));
  };

  const generateDefaultSchema = () => {
    const defaultSchema = {
      '@context': 'https://schema.org',
      '@type': settings?.schema_type || 'Product',
      name: settings?.site_name || 'Product Name',
      description: settings?.meta_description || 'Product description',
      brand: {
        '@type': 'Brand',
        name: settings?.site_name || 'Brand Name',
      },
      offers: {
        '@type': 'AggregateOffer',
        priceCurrency: 'USD',
        lowPrice: '49',
        highPrice: '69',
        offerCount: '3',
        availability: 'https://schema.org/InStock',
      },
      aggregateRating: {
        '@type': 'AggregateRating',
        ratingValue: '4.8',
        bestRating: '5',
        worstRating: '1',
        reviewCount: '1247',
      },
      review: [
        {
          '@type': 'Review',
          author: { '@type': 'Person', name: 'Customer Name' },
          reviewRating: { '@type': 'Rating', ratingValue: '5', bestRating: '5' },
          reviewBody: 'Great product, highly recommended!'
        }
      ]
    };
    setSchemaText(JSON.stringify(defaultSchema, null, 2));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-4 border-slate-200 border-t-blue-600" />
      </div>
    );
  }

  return (
    <div className="space-y-8" data-testid="admin-seo">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-heading text-3xl font-bold text-slate-900">SEO & Schema</h1>
          <p className="mt-2 text-slate-600">Optimize for search engines and rich results</p>
        </div>
        <Button onClick={handleSave} disabled={saving} data-testid="save-seo-btn">
          <Save className="w-4 h-4 mr-2" />
          {saving ? 'Saving...' : 'Save Changes'}
        </Button>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Meta Tags */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Search className="w-5 h-5" />
              Meta Tags
            </CardTitle>
            <CardDescription>Basic SEO meta information</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>Meta Title</Label>
              <Input
                value={settings?.meta_title || ''}
                onChange={(e) => updateField('meta_title', e.target.value)}
                placeholder="Product Name - Official Website 2026"
                data-testid="meta-title-input"
              />
              <p className="text-xs text-slate-500">
                {(settings?.meta_title || '').length}/60 characters
              </p>
            </div>
            <div className="space-y-2">
              <Label>Meta Description</Label>
              <Textarea
                value={settings?.meta_description || ''}
                onChange={(e) => updateField('meta_description', e.target.value)}
                placeholder="Compelling description for search results..."
                rows={3}
              />
              <p className="text-xs text-slate-500">
                {(settings?.meta_description || '').length}/160 characters
              </p>
            </div>
            <div className="space-y-2">
              <Label>Meta Keywords</Label>
              <Input
                value={settings?.meta_keywords || ''}
                onChange={(e) => updateField('meta_keywords', e.target.value)}
                placeholder="keyword1, keyword2, keyword3"
              />
            </div>
          </CardContent>
        </Card>

        {/* Open Graph */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Globe className="w-5 h-5" />
              Social Sharing
            </CardTitle>
            <CardDescription>Open Graph and Twitter Card settings</CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <Label>OG Image URL</Label>
              <Input
                value={settings?.og_image || ''}
                onChange={(e) => updateField('og_image', e.target.value)}
                placeholder="https://example.com/og-image.jpg"
              />
              <p className="text-xs text-slate-500">Recommended: 1200x630 pixels</p>
            </div>
            <div className="space-y-2">
              <Label>Twitter Card Type</Label>
              <Select
                value={settings?.twitter_card || 'summary_large_image'}
                onValueChange={(value) => updateField('twitter_card', value)}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="summary">Summary</SelectItem>
                  <SelectItem value="summary_large_image">Summary Large Image</SelectItem>
                  <SelectItem value="app">App</SelectItem>
                  <SelectItem value="player">Player</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Schema Markup */}
        <Card className="lg:col-span-2">
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="flex items-center gap-2">
                  <Code className="w-5 h-5" />
                  Schema.org Markup (JSON-LD)
                </CardTitle>
                <CardDescription>Structured data for rich results in search</CardDescription>
              </div>
              <div className="flex gap-2">
                <Select
                  value={settings?.schema_type || 'Product'}
                  onValueChange={(value) => updateField('schema_type', value)}
                >
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Product">Product</SelectItem>
                    <SelectItem value="Organization">Organization</SelectItem>
                    <SelectItem value="WebSite">WebSite</SelectItem>
                    <SelectItem value="LocalBusiness">LocalBusiness</SelectItem>
                    <SelectItem value="Article">Article</SelectItem>
                  </SelectContent>
                </Select>
                <Button variant="outline" onClick={generateDefaultSchema}>
                  Generate Template
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            <Textarea
              value={schemaText}
              onChange={(e) => setSchemaText(e.target.value)}
              rows={20}
              className="font-mono text-sm"
              placeholder='{"@context": "https://schema.org", ...}'
              data-testid="schema-textarea"
            />
            <p className="mt-2 text-xs text-slate-500">
              Test your schema at{' '}
              <a
                href="https://search.google.com/test/rich-results"
                target="_blank"
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline"
              >
                Google Rich Results Test
              </a>
            </p>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default AdminSEO;
